import React, { ReactNode, useState } from 'react';
import { Card, Form, Button, Row, Col, Space } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { FormInstance } from 'antd/es/form';

interface FilterPanelProps {
  children: ReactNode;
  onFilter?: (values: any) => void;
  onReset?: () => void;
  initialValues?: Record<string, any>;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  title?: ReactNode;
  extra?: ReactNode;
  className?: string;
  loading?: boolean;
  form?: FormInstance; // 添加form属性支持
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  children,
  onFilter,
  onReset,
  initialValues = {},
  collapsible = true,
  defaultCollapsed = false,
  title,
  extra,
  className = '',
  loading = false,
  form: externalForm, // 接收外部传入的form实例
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  
  // 使用外部传入的form实例，如果没有则使用内部创建的form实例
  const usedForm = externalForm || form;

  // 使用传入的title，如果没有传入则使用默认的国际化文案
  const panelTitle = title || t('common.filterConditions');

  // 处理筛选
  const handleFilter = () => {
    const values = usedForm.getFieldsValue();
    onFilter?.(values);
  };

  // 处理重置
  const handleReset = () => {
    // 直接重置表单，而不是使用initialValues
    usedForm.resetFields();
    onReset?.();
  };

  // 切换折叠状态
  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  // 渲染内容
  const renderContent = () => (
    <Form
      form={usedForm}
      layout="vertical"
      initialValues={initialValues}
      className="filter-panel-form"
    >
      <Row gutter={[16, 8]}>
        {children}
      </Row>
      <Row justify="end" className="filter-panel-actions">
        <Space>
          <Button onClick={handleReset}>{t('common.reset')}</Button>
          <Button type="primary" onClick={handleFilter} loading={loading}>{t('common.filter')}</Button>
        </Space>
      </Row>
    </Form>
  );

  // 如果不可折叠，直接渲染内容
  if (!collapsible) {
    return (
      <Card
        extra={extra}
        className={`filter-panel ${className}`}
      >
        {renderContent()}
      </Card>
    );
  }

  // 可折叠模式
  return (
    <Card
      className={`filter-panel ${className}`}
      title={null}
      styles={{ body: collapsed ? { padding: 0 } : undefined }}
      extra={
        <div className="filter-panel-header">
          {extra}
          <Button
            type="link"
            icon={collapsed ? <DownOutlined /> : <UpOutlined />}
            onClick={toggleCollapse}
          >
            {collapsed ? t('common.expand') : t('common.collapse')}
          </Button>
        </div>
      }
    >
      {!collapsed && renderContent()}
    </Card>
  );
};

export default FilterPanel;